import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Database, Download, Upload, RefreshCw, AlertTriangle, CheckCircle, Loader2, HardDrive, Clock, Calendar, Mail, Bell, Settings, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import BackupSchedulerConfig from './BackupSchedulerConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GestionBackups({ getAuthHeader }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [backing, setBacking] = useState(false);
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  
  // Configuration scheduler
  const [schedulerConfig, setSchedulerConfig] = useState({
    enabled: false,
    schedule_hour: 0,
    schedule_minute: 0,
    notification_emails: [],
    notify_on_success: false,
    last_backup: null,
    last_status: null
  });
  const [newEmail, setNewEmail] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingNotification, setTestingNotification] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchSchedulerConfig();
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/seed/status`, {
        headers: getAuthHeader()
      });
      setStatus(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux super admins');
      } else {
        toast.error('Erreur lors du chargement du statut');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/seed/scheduler/config`, {
        headers: getAuthHeader()
      });
      setSchedulerConfig(response.data);
    } catch {
    }
  };

  const handleBackup = async () => {
    setBacking(true);
    try {
      const _response = await axios.post(`${BACKEND_URL}/api/admin/seed/backup`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Backup créé avec succès !');
      fetchStatus();
      fetchSchedulerConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du backup');
    } finally {
      setBacking(false);
    }
  };

  const handleRestore = async (force = false) => {
    setRestoring(true);
    setShowForceConfirm(false);
    try {
      const endpoint = force ? '/api/admin/seed/restore-force' : '/api/admin/seed/restore';
      await axios.post(`${BACKEND_URL}${endpoint}`, {}, {
        headers: getAuthHeader()
      });
      
      if (force) {
        toast.success('Données restaurées avec succès (mode forcé)');
      } else {
        toast.success('Restauration terminée');
      }
      
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setRestoring(false);
    }
  };

  const saveSchedulerConfig = async () => {
    setSavingConfig(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/seed/scheduler/config`, schedulerConfig, {
        headers: getAuthHeader()
      });
      setSchedulerConfig(response.data);
      toast.success('Configuration sauvegardée !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingConfig(false);
    }
  };

  const addEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      if (!schedulerConfig.notification_emails.includes(newEmail)) {
        setSchedulerConfig(prev => ({
          ...prev,
          notification_emails: [...prev.notification_emails, newEmail]
        }));
        setNewEmail('');
      }
    }
  };

  const removeEmail = (email) => {
    setSchedulerConfig(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== email)
    }));
  };

  const testNotification = async () => {
    setTestingNotification(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/seed/scheduler/test-notification`, {}, {
        headers: getAuthHeader()
      });
      const data = res.data || {};
      const sent = data.sent || 0;
      const failed = data.failed || 0;
      if (data.status === 'success' && sent > 0) {
        toast.success(`✉️ ${sent} email(s) de test envoyé(s) avec succès !`, {
          description: 'Vérifiez votre boîte de réception (et les spams).',
          duration: 6000,
        });
      } else if (data.status === 'partial') {
        toast.warning(`Partiellement envoyé : ${sent} OK, ${failed} échec(s)`, {
          description: (data.errors || []).slice(0, 2).join(' · '),
          duration: 8000,
        });
      } else {
        toast.error('Aucune notification envoyée');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Erreur lors de l'envoi de la notification", { duration: 8000 });
    } finally {
      setTestingNotification(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    try {
      return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Chargement du statut...</span>
      </div>
    );
  }

  const dbCounts = status?.database?.current_counts || {};
  const seedCounts = status?.seed_file?.counts || {};
  const seedExists = status?.seed_file?.exists;

  const collections = [
    { key: 'users', label: 'Utilisateurs', icon: '👤' },
    { key: 'coopers', label: "COOPER'S", icon: '🏢' },
    { key: 'members', label: 'Membres', icon: '👥' },
    { key: 'experts', label: 'Experts', icon: '🎓' },
    { key: 'souscriptions', label: 'Souscriptions', icon: '📄' },
    { key: 'nocode_simulators', label: 'Simulateurs', icon: '🧮' },
    { key: 'nocode_rules', label: 'Règles métier', icon: '⚙️' },
    { key: 'dispositifs', label: 'Dispositifs', icon: '📋' },
    { key: 'activites', label: 'Activités', icon: '📊' }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Database className="h-7 w-7 text-blue-600" />
            Gestion des Backups
          </h2>
          <p className="text-gray-600 mt-1">
            Sauvegardez et restaurez les données de la plateforme
          </p>
        </div>
        <Button onClick={fetchStatus} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Statut général */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Base de données actuelle */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-blue-600" />
              Base de données
            </CardTitle>
            <CardDescription>{status?.database?.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {formatNumber(Object.values(dbCounts).reduce((a, b) => a + b, 0))}
            </div>
            <p className="text-sm text-gray-500">documents au total</p>
          </CardContent>
        </Card>

        {/* Fichier de backup */}
        <Card className={`border-l-4 ${seedExists ? 'border-l-green-500' : 'border-l-orange-500'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="h-5 w-5 text-green-600" />
              Fichier backup
            </CardTitle>
            <CardDescription>
              {seedExists ? 'seed_data.json' : 'Aucun backup'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {seedExists ? (
              <>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {formatNumber(Object.values(seedCounts).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0))}
                </div>
                <p className="text-sm text-gray-500">documents sauvegardés</p>
              </>
            ) : (
              <div className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="h-5 w-5" />
                <span>Aucun backup</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statut backup automatique */}
        <Card className={`border-l-4 ${schedulerConfig.enabled ? 'border-l-purple-500' : 'border-l-gray-400'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Backup auto
            </CardTitle>
            <CardDescription>
              {schedulerConfig.enabled 
                ? `Tous les jours à ${String(schedulerConfig.schedule_hour).padStart(2, '0')}:${String(schedulerConfig.schedule_minute).padStart(2, '0')}`
                : 'Désactivé'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {schedulerConfig.last_backup ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  {schedulerConfig.last_status === 'success' ? (
                    <Badge className="bg-green-600 text-white font-semibold">Succès</Badge>
                  ) : schedulerConfig.last_status === 'error' ? (
                    <Badge className="bg-red-600 text-white font-semibold">Échec</Badge>
                  ) : (
                    <Badge className="bg-gray-500 text-white font-semibold">Inconnu</Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Dernier: {formatDate(schedulerConfig.last_backup)}
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Aucun backup automatique effectué</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Actions manuelles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions manuelles</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={handleBackup} 
              disabled={backing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {backing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {backing ? 'Sauvegarde...' : 'Créer un backup'}
            </Button>

            <Button 
              onClick={() => handleRestore(false)} 
              disabled={restoring || !seedExists}
              variant="outline"
              className="border-green-500 text-green-700 hover:bg-green-50"
            >
              {restoring ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Restaurer (safe)
            </Button>

            <Button 
              onClick={() => setShowForceConfirm(true)} 
              disabled={restoring || !seedExists}
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-50"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Restauration forcée
            </Button>
          </div>

          {showForceConfirm && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-800">Attention : Action irréversible</h4>
                  <p className="text-sm text-red-700 mt-1">
                    Cette action va <strong>supprimer toutes les données actuelles</strong> et les remplacer 
                    par celles du fichier de backup.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      onClick={() => handleRestore(true)} 
                      disabled={restoring}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {restoring && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Confirmer
                    </Button>
                    <Button 
                      onClick={() => setShowForceConfirm(false)} 
                      variant="outline"
                      size="sm"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration backup automatique */}
      <BackupSchedulerConfig
        schedulerConfig={schedulerConfig}
        setSchedulerConfig={setSchedulerConfig}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        savingConfig={savingConfig}
        saveSchedulerConfig={saveSchedulerConfig}
        testingNotification={testingNotification}
        testNotification={testNotification}
        addEmail={addEmail}
        removeEmail={removeEmail}
      />

      {/* Détail par collection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Détail par collection</CardTitle>
          <CardDescription>
            Comparaison entre la base de données actuelle et le fichier de backup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Collection</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Base actuelle</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Backup</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Statut</th>
                </tr>
              </thead>
              <tbody>
                {collections.map(({ key, label, icon }) => {
                  const dbCount = dbCounts[key] || 0;
                  const seedCount = seedCounts[key] || 0;
                  const isSync = dbCount === seedCount;
                  const isEmpty = dbCount === 0;
                  
                  return (
                    <tr key={key} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2">
                          <span>{icon}</span>
                          <span className="font-medium">{label}</span>
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-mono ${isEmpty ? 'text-orange-600' : 'text-gray-900'}`}>
                          {formatNumber(dbCount)}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="font-mono text-gray-600">
                          {formatNumber(seedCount)}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4">
                        {isEmpty ? (
                          <Badge className="bg-orange-600 text-white border-orange-700 hover:bg-orange-700 font-semibold">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Vide
                          </Badge>
                        ) : isSync ? (
                          <Badge className="bg-green-600 text-white border-green-700 hover:bg-green-700 font-semibold">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Synchronisé
                          </Badge>
                        ) : (
                          <Badge className="bg-blue-600 text-white border-blue-700 hover:bg-blue-700 font-semibold">
                            <Clock className="h-3 w-3 mr-1" />
                            Différent
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Informations */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">À propos des notifications</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Les emails de notification sont envoyés <strong>uniquement en cas d'échec</strong> du backup (sauf si option activée)</li>
                <li>Ajoutez plusieurs emails pour une meilleure couverture</li>
                <li>Testez les notifications pour vérifier la configuration</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
